import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { Point } from "ol/geom";
import Style from "ol/style/Style";
import Icon from "ol/style/Icon";
import { Feature } from "ol";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { fromLonLat } from "ol/proj";
import { boundingExtent } from "ol/extent";
import Overlay from "ol/Overlay";
import { DevicePositionService } from "src/services/device-position/device_position.service";
import { Store } from "@ngrx/store";
import { AppState } from "src/app/store/app.state";
import { Subscription } from "rxjs";
import Cluster from "ol/source/Cluster";
import { Circle as CircleStyle, Fill, Stroke, Text } from "ol/style";
import { Coordinate } from "ol/coordinate";
import { Router } from "@angular/router";
import { MapEventService } from "src/services/event/map-event.service";

export interface DevicePosition {
  DeviceId: string;
  AssetType: string;
  LocationStatus?: {
    last_update: string;
    position?: {
      latitude: number | null;
      longitude: number | null;
    };
    status: "unknown" | "pending" | "located";
  };
}

interface DeviceCategories {
  unknownDevices: DevicePosition[];
  pendingDevices: DevicePosition[];
  positionedDevices: DevicePosition[];
  loraDevices: DevicePosition[];
  unknownDevicesNeverUpdated: DevicePosition[];
}

@Component({
  selector: "app-openlayersmap",
  templateUrl: "./openlayersmap.component.html",
  styleUrls: ["./openlayersmap.component.scss"],
})
export class OpenlayersmapComponent implements AfterViewInit, OnInit, OnDestroy {
  @ViewChild("mapContainer", { static: false }) mapContainer!: ElementRef;
  @ViewChild("popup", { static: false }) popupElementRef!: ElementRef;

  popupOverlay!: Overlay;
  map!: Map;
  lastZoomedCoordinate: Coordinate | null = null;
  
  showPopup: boolean = false;
  popupDeviceIds: string[] = [];
  popupPosition: [number, number] = [0, 0];

  unknownDevicesNeverUpdatedCount: number = 0;

  companyId: string = "";
  onLocateNewDevicesClicked: boolean = false;
  private storeSubscription: Subscription | null = null;
  private clickTimeout?: ReturnType<typeof setTimeout>;
  
  private clustersLayer: VectorLayer<any> | null = null;

  constructor(
    private devicePositionService: DevicePositionService,
    private store: Store<AppState>,
    public route: Router,
    private cdr: ChangeDetectorRef,
    private mapEventService: MapEventService
  ) {}

  ngOnInit(): void {
    this.storeSubscription = this.store
      .select((state) => state.company?.companyId)
      .subscribe((id) => {
        if (id && id !== this.companyId) {
          this.companyId = id;
          this.fetchDevicePositions();
        }
      });
  }

  ngAfterViewInit(): void {
    this.map = new Map({
      target: this.mapContainer.nativeElement,
      layers: [
        new TileLayer({
          source: new OSM(),
        }),
      ],
      view: new View({
        center: fromLonLat([77, 28]),
        zoom: 6,
      }),
    });

    this.popupOverlay = new Overlay({
      element: this.popupElementRef.nativeElement,
      positioning: "bottom-center",
      stopEvent: false,
      offset: [0, -20],
    });
    this.map.addOverlay(this.popupOverlay);
  }

  ngOnDestroy(): void {
    if (this.storeSubscription) {
      this.storeSubscription.unsubscribe();
    }
    
    if (this.clickTimeout) {
      clearTimeout(this.clickTimeout);
    }
    
    if (this.clustersLayer) {
      this.map.removeLayer(this.clustersLayer);
    }
  }

  fetchDevicePositions(): void {
    this.devicePositionService.getDevicePosition(this.companyId).subscribe({
      next: (response) => {
        if (response?.data?.Devices && Array.isArray(response.data.Devices)) {
          this.processDevices(response.data.Devices);
        }
      },
      error: (error) => {
        console.error("Error fetching device positions:", error);
      },
    });
  }

  onLocateNewDevicesClick(): void {
    if (!this.onLocateNewDevicesClicked) {
      this.devicePositionService.getDevicePosition(this.companyId).subscribe({
        next: (response) => {
          if (response?.data?.Devices && Array.isArray(response.data.Devices)) {
            const categories = this.categorizeDevices(response.data.Devices);
            this.callAllUnknownNeverUpdated(response.data.Devices);
          }
        },
        error: (error) => {
          console.error(
            "Error fetching device positions on button click:",
            error
          );
        },
      });
      this.onLocateNewDevicesClicked = true;
    } else {
      console.log("Already clicked, no need to fetch again.");
    }
  }

  categorizeDevices(devices: DevicePosition[]): DeviceCategories {
    const categories: DeviceCategories = {
      unknownDevices: [],
      pendingDevices: [],
      positionedDevices: [],
      loraDevices: [],
      unknownDevicesNeverUpdated: [],
    };

    if (!devices || devices.length === 0) {
      return categories;
    }

    devices.forEach((device) => {
      if (device.AssetType == "4G") {
        if (device.LocationStatus?.status === "unknown") {
          this.updateDeviceLocationStatus(device.DeviceId);
          categories.unknownDevices.push(device);
          if (!device.LocationStatus.last_update) {
            categories.unknownDevicesNeverUpdated.push(device);
          }
        } else if (device.LocationStatus?.status === "pending") {
          categories.pendingDevices.push(device);
        }

        if (
          device.LocationStatus?.position?.latitude !== null &&
          device.LocationStatus?.position?.longitude !== null
        ) {
          categories.positionedDevices.push(device);
        }
      } else {
        categories.loraDevices.push(device);
      }
    });
    
    this.unknownDevicesNeverUpdatedCount = categories.unknownDevicesNeverUpdated.length;
    this.addMarkersFromDevices(
      categories.positionedDevices.map((device) => ({
        id: device.DeviceId,
        lat: device.LocationStatus!.position!.latitude!,
        lon: device.LocationStatus!.position!.longitude!,
      }))
    );
    return categories;
  }

  processDevices(devices: DevicePosition[]): void {
    const categories = this.categorizeDevices(devices);

    console.log(
      `Devices with unknown status: ${categories.unknownDevices.length}`,
      categories.unknownDevices
    );
    console.log(
      `Devices with unknown status never updated: ${categories.unknownDevicesNeverUpdated.length}`,
      categories.unknownDevicesNeverUpdated
    );
    console.log(
      `Devices with pending status: ${categories.pendingDevices.length}`,
      categories.pendingDevices
    );
    console.log(
      `Devices with position: ${categories.positionedDevices.length}`,
      categories.positionedDevices
    );
    console.log(
      `Lora devices: ${categories.loraDevices.length}`,
      categories.loraDevices
    );
  }

  callAllUnknownNeverUpdated(devices: DevicePosition[]): void {
    if (!devices || devices.length === 0) {
      console.log("No devices available for processing");
      return;
    }

    const categories = this.categorizeDevices(devices);
    const unknownDevicesNeverUpdated = categories.unknownDevicesNeverUpdated;

    console.log(
      `Number of devices with unknown status and no location: ${unknownDevicesNeverUpdated.length}`
    );

    if (unknownDevicesNeverUpdated.length === 0) {
      console.log("No devices with 'unknown' status and no location found");
      return;
    }

    unknownDevicesNeverUpdated.forEach((device) => {
      console.log(`Updating status for device ${device.DeviceId}`);
      this.updateDeviceLocationStatus(device.DeviceId);
    });

    console.log(
      `Status update initiated for ${unknownDevicesNeverUpdated.length} devices`
    );
  }

  updateDeviceLocationStatus(deviceId: string): void {
    this.devicePositionService
      .updateDevicePosition(this.companyId, deviceId)
      .subscribe({
        next: (response) => {
          console.log("Position updated", response);
        },
        error: (error) => {
          console.error("Erreur while updated:", error);
        },
      });
  }

  addMarkersFromDevices(
    devices: { id: string; lon: number; lat: number }[]
  ): void {
    if (this.clustersLayer) {
      this.map.removeLayer(this.clustersLayer);
      this.clustersLayer = null;
    }

    this.showPopup = false;
    this.popupOverlay.setPosition(undefined);

    const features = devices.map((device) => {
      const feature = new Feature({
        geometry: new Point(fromLonLat([device.lon, device.lat])),
        id: device.id,
      });
      return feature;
    });

    const source = new VectorSource({ features });

    const clusterSource = new Cluster({
      distance: 40,
      source: source,
    });

    const styleCache: { [key: number]: Style } = {};

    this.clustersLayer = new VectorLayer({
      source: clusterSource,
      style: function (feature) {
        const size = feature.get("features").length;
        if (!styleCache[size]) {
          styleCache[size] = new Style({
            image: new CircleStyle({
              radius: 15,
              stroke: new Stroke({ color: "#fff" }),
              fill: new Fill({ color: "#3399CC" }),
            }),
            text: new Text({
              text: size.toString(),
              fill: new Fill({ color: "#fff" }),
            }),
          });
        }
        return styleCache[size];
      },
    });

    this.map.addLayer(this.clustersLayer);
    this.map.setView(
      new View({
        center: fromLonLat([10, 50]),
        zoom: 3,
      })
    );

    this.map.on("singleclick", (evt) => {
      const feature = this.map.forEachFeatureAtPixel(evt.pixel, (f) => f);

      if (!feature) {
        this.showPopup = false;
        this.popupOverlay.setPosition(undefined);
        this.cdr.detectChanges();
        return;
      }

      const clusteredFeatures = feature.get("features");
      const coordinates = (feature.getGeometry() as Point).getCoordinates();

      this.popupDeviceIds = clusteredFeatures.map((f: any) => f.get("id"));
      this.popupPosition = [coordinates[0], coordinates[1]];
      this.showPopup = true;

      this.popupOverlay.setPosition(coordinates);
      this.cdr.detectChanges();

      const shouldZoom = clusteredFeatures.length > 1;
      if (shouldZoom) {
        if (this.clickTimeout) {
          clearTimeout(this.clickTimeout);
        }
        this.clickTimeout = setTimeout(() => {
          const firstCoords = clusteredFeatures[0]
            .getGeometry()
            .getCoordinates();
          const allSameCoords = clusteredFeatures.every((f: any) => {
            const coords = f.getGeometry().getCoordinates();
            return (
              Math.abs(coords[0] - firstCoords[0]) < 0.01 &&
              Math.abs(coords[1] - firstCoords[1]) < 0.01
            );
          });

          const view = this.map.getView();
          if (allSameCoords) {
            view.animate({ center: coordinates, duration: 400 });
          } else {
            view.animate({
              center: coordinates,
              zoom: view.getZoom()! + 4,
              duration: 400,
            });
          }
        }, 100);
      }
    });
  }

  onDeviceClick(deviceId: string): void {
    this.mapEventService.emitDeviceClick(deviceId);
    
    this.showPopup = false;
    this.popupOverlay.setPosition(undefined);
  }
}